/**
 * printerSelection.ts
 *
 * Gestión de estado persistente para selección de destino de impresión.
 * Almacena en localStorage qué método usar: SUNMI integrada, BLE externo, o navegador.
 */

import { isNativePlatform } from '../platform';
import type { SavedBlePrinter } from './blePrinter';
import { getSavedBlePrinter } from './blePrinter';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PrintDestination = 'sunmi' | 'bluetooth-ble' | 'browser';

export interface PrinterConfig {
  destination: PrintDestination;
  blePrinter: SavedBlePrinter | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LS_DESTINATION_KEY = 'tiempos_print_destination';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getSelectedDestination(): PrintDestination {
  try {
    const stored = localStorage.getItem(LS_DESTINATION_KEY);
    if (stored === 'sunmi' || stored === 'bluetooth-ble' || stored === 'browser') {
      return stored;
    }
  } catch { /* ignore */ }

  // Default: sunmi if native Android, browser otherwise
  return isNativePlatform() ? 'sunmi' : 'browser';
}

export function setSelectedDestination(dest: PrintDestination): void {
  localStorage.setItem(LS_DESTINATION_KEY, dest);
}

export function getPrinterConfig(): PrinterConfig {
  return {
    destination: getSelectedDestination(),
    blePrinter: getSavedBlePrinter(),
  };
}

export function getDestinationLabel(dest?: PrintDestination): string {
  const d = dest || getSelectedDestination();
  switch (d) {
    case 'sunmi': return 'SUNMI Integrada';
    case 'bluetooth-ble': return 'Bluetooth Externa';
    case 'browser': return 'Navegador';
    default: return d;
  }
}
